import {
  IAbbreviationWirePayload,
  IGeneralThemeNumberFormatEntry,
  IGeneralThemeTimeFormat
} from 'components/Theme/GeneralSettings/generalTheme.types';

import { fromWirePayload } from './components/AbbreviationsSettingsV2/AbbreviationsSettingsV2.payload';
import { defaultStoreSettings } from './StoreSettings.consts';
import {
  EOfferFormatKey,
  ETimeAbbreviationStyle,
  ETimeBaseUnit,
  INumberFormat,
  INumberFormatEntry,
  IQuantityFormat,
  IStoreSettingsFormValues,
  ITimeFormat
} from './StoreSettings.types';

const buildQuantityFormat = (
  source: Partial<IQuantityFormat> | undefined,
  fallback: IQuantityFormat
): IQuantityFormat => ({
  milSeparator: source?.milSeparator || fallback.milSeparator,
  fractionalSeparator: source?.fractionalSeparator || fallback.fractionalSeparator,
  thousandShorthand: source?.thousandShorthand ?? fallback.thousandShorthand,
  millionShorthand: source?.millionShorthand ?? fallback.millionShorthand,
  billionShorthand: source?.billionShorthand ?? fallback.billionShorthand,
  trillionShorthand: source?.trillionShorthand ?? fallback.trillionShorthand,
  quadrillionShorthand:
    source?.quadrillionShorthand ?? fallback.quadrillionShorthand,
  quintillionShorthand:
    source?.quintillionShorthand ?? fallback.quintillionShorthand,
  sextillionShorthand:
    source?.sextillionShorthand ?? fallback.sextillionShorthand,
  septillionShorthand:
    source?.septillionShorthand ?? fallback.septillionShorthand,
  digitsBasedAbbreviation:
    source?.digitsBasedAbbreviation ?? fallback.digitsBasedAbbreviation,
  maxDigitsWithoutShorthand:
    source?.maxDigitsWithoutShorthand ?? fallback.maxDigitsWithoutShorthand
});

const toBaseUnit = (
  value: string | undefined,
  fallback: ETimeBaseUnit
): ETimeBaseUnit => {
  const isKnownBaseUnit = Object.values(ETimeBaseUnit).includes(
    value as ETimeBaseUnit
  );
  return isKnownBaseUnit ? (value as ETimeBaseUnit) : fallback;
};

const toAbbreviationStyle = (
  value: string | undefined,
  fallback: ETimeAbbreviationStyle
): ETimeAbbreviationStyle => {
  const isKnownStyle = Object.values(ETimeAbbreviationStyle).includes(
    value as ETimeAbbreviationStyle
  );
  return isKnownStyle ? (value as ETimeAbbreviationStyle) : fallback;
};

const buildTimeFormat = (
  source: IGeneralThemeTimeFormat | undefined,
  fallback: ITimeFormat
): ITimeFormat => {
  let baseUnitThreshold = fallback.baseUnitThreshold;
  if (source?.baseUnitThreshold !== undefined) {
    baseUnitThreshold = source.baseUnitThreshold;
  }
  return {
    baseUnitThreshold,
    baseUnit: toBaseUnit(source?.baseUnit, fallback.baseUnit),
    abbreviationStyle: toAbbreviationStyle(
      source?.abbreviationStyle,
      fallback.abbreviationStyle
    )
  };
};

const buildNumberFormatEntry = (
  source: IGeneralThemeNumberFormatEntry | undefined,
  fallback: INumberFormatEntry
): INumberFormatEntry => ({
  quantity: buildQuantityFormat(source?.quantity, fallback.quantity),
  time: buildTimeFormat(source?.time, fallback.time)
});

export const buildStoreSettingsInitialValues = (
  data?: IAbbreviationWirePayload
): IStoreSettingsFormValues => {
  const numberFormatSource = fromWirePayload(data);
  const numberFormat = Object.values(EOfferFormatKey).reduce<INumberFormat>(
    (accumulator, offer) => {
      accumulator[offer] = buildNumberFormatEntry(
        numberFormatSource?.[offer],
        defaultStoreSettings.numberFormat[offer]
      );
      return accumulator;
    },
    {} as INumberFormat
  );

  return { numberFormat };
};

export const applyQuantityFieldChange = (
  setFieldValue: (field: string, value: boolean | string) => void,
  fieldPath: string,
  rawValue: string
): void => {
  if (rawValue === 'true' || rawValue === 'false') {
    setFieldValue(fieldPath, rawValue === 'true');
    return;
  }
  setFieldValue(fieldPath, rawValue);
};
